import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { supabase } from '../lib/supabase';

interface LeaderboardEntry {
  rank: number;
  userId: string;
  displayName: string;
  score: number;
  searches: number;
  conceptsClicked: number;
  timeSpent: number;
  isCurrentUser: boolean;
}

interface UserStats {
  rank: number;
  totalUsers: number;
  displayName: string;
  total_score: number;
  total_searches: number;
  concepts_clicked: number;
  time_spent_minutes: number;
}

const Container = styled.div`
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  padding: var(--gap-xl);
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: var(--gap-lg);
    margin: var(--gap-md);
    width: calc(100% - 2 * var(--gap-md));
    max-height: 90vh;
    overflow-y: auto;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gap-xl);
`;

const Title = styled.h2`
  font-family: var(--font-heading);
  font-size: var(--font-size-xl);
  color: var(--color-text);
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: var(--color-text-secondary);
  font-size: var(--font-size-xl);
  cursor: pointer;
  padding: 8px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }
`;

const UserStatsCard = styled.div`
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--gap-lg);
  margin-bottom: var(--gap-xl);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: var(--gap-md);
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
    gap: var(--gap-sm);
    padding: var(--gap-md);
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-family: var(--font-heading);
  font-size: var(--font-size-lg);
  color: var(--color-primary);
  font-weight: var(--font-weight-bold);
`;

const StatLabel = styled.div`
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: var(--letter-spacing-wide);
  margin-top: 4px;
`;

const LeaderboardTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--gap-sm);
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 100px 100px 100px 100px;
  gap: var(--gap-md);
  padding: var(--gap-md);
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: var(--letter-spacing-wide);
  border-bottom: 1px solid var(--color-border);

  @media (max-width: 768px) {
    display: none;
  }
`;

const LeaderboardRow = styled.div<{ isCurrentUser?: boolean; rank?: number }>`
  display: grid;
  grid-template-columns: 60px 1fr 100px 100px 100px 100px;
  gap: var(--gap-md);
  padding: var(--gap-md);
  background: ${props => props.isCurrentUser ? 'var(--color-primary-light)' : 'transparent'};
  border-radius: var(--radius-sm);
  transition: background 0.2s;

  ${props => !props.isCurrentUser && `
    &:hover {
      background: var(--color-surface-hover);
    }
  `}


  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--gap-sm);
    padding: var(--gap-lg);
    border: 1px solid var(--color-border);
    margin-bottom: var(--gap-sm);
  }
`;

const Rank = styled.div<{ rank?: number }>`
  font-family: var(--font-heading);
  font-weight: var(--font-weight-bold);
  color: var(--color-text);
  text-align: center;
  
  @media (max-width: 768px) {
    display: none;
  }
`;


const Score = styled.div`
  font-family: var(--font-heading);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
  text-align: right;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Stat = styled.div`
  color: var(--color-text-secondary);
  text-align: right;
  font-size: var(--font-size-sm);
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  color: var(--color-text-secondary);
  padding: var(--gap-xl);
`;

const MobileCardInfo = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: var(--gap-sm);
  }
`;

const MobileCardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MobileLabel = styled.span`
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: var(--letter-spacing-wide);
`;

const MobileValue = styled.span`
  font-weight: var(--font-weight-medium);
  color: var(--color-text);
`;

const W1Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ffd700, #ffb347);
  color: #8b4513;
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 8px;
  min-width: 24px;
  height: 18px;
  box-shadow: 0 1px 3px rgba(255, 215, 0, 0.3);
  border: 1px solid rgba(255, 215, 0, 0.5);
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.1);
`;

const NameWithBadge = styled.div`
  display: flex;
  align-items: center;
  min-width: 0; /* Allow flex shrinking */
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const NameText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1; /* Allow name to shrink */
  min-width: 0;
`;

const BadgeContainer = styled.span`
  flex-shrink: 0; /* Never shrink the badge */
  margin-left: 8px;
`;

// Week 1 winners (top 5)
const WEEK_1_WINNERS = [
  'Poggins',
  'realdaenerys', 
  'locksleyofrobin',
  'yippeemira',
  'the.ghost.with.extra.toast'
];

// Week 2 winners (top 5 excluding W1 winners)
const WEEK_2_WINNERS = [
  'bcendoma',
  'lilpeachyy_',
  'toxicdonut2197',
  'monreaper243',
  '_.eczlippse'
];

// Week 3 winners (top 4 + spectralgh0ul and rinns, excluding valekorr170)
const WEEK_3_WINNERS = [
  'spookygf',
  'shidoitsult', 
  'vexyprx',
  'spectralgh0ul',
  'rinns'
];

interface LeaderboardProps {
  onClose?: () => void;
}

type ViewType = 'all-time' | 'weekly';

const ViewToggle = styled.div`
  display: flex;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 4px;
  margin-bottom: var(--gap-lg);
`;

const ViewToggleButton = styled.button<{ active: boolean }>`
  flex: 1;
  padding: var(--gap-sm) var(--gap-md);
  background: ${props => props.active ? 'var(--color-primary)' : 'transparent'};
  color: ${props => props.active ? 'white' : 'var(--color-text)'};
  border: none;
  border-radius: var(--radius-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? 'var(--color-primary)' : 'var(--color-surface-hover)'};
  }
`;

export const Leaderboard: React.FC<LeaderboardProps> = ({ onClose }) => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState<ViewType>('all-time');

  useEffect(() => {
    loadLeaderboard();
  }, [viewType]);

  const loadLeaderboard = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Get leaderboard
      const { data: leaderboardData, error: leaderboardError } = await supabase.functions.invoke('leaderboard', {
        body: { viewType },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (leaderboardError) throw leaderboardError;
      console.log('Leaderboard data received:', leaderboardData);
      setLeaderboard(leaderboardData || []);

      // Get user stats
      const { data: userData, error: userError } = await supabase.functions.invoke('leaderboard', {
        body: { type: 'user', viewType },
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (userError) throw userError;
      console.log('User stats received:', userData);
      setUserStats(userData);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Loading leaderboard...</LoadingMessage>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>🏆 Lore Spirals Leaderboard</Title>
        {onClose && (
          <CloseButton onClick={onClose}>×</CloseButton>
        )}
      </Header>

      <ViewToggle>
        <ViewToggleButton 
          active={viewType === 'all-time'} 
          onClick={() => setViewType('all-time')}
        >
          All Time
        </ViewToggleButton>
        <ViewToggleButton 
          active={viewType === 'weekly'} 
          onClick={() => setViewType('weekly')}
        >
          This Week
        </ViewToggleButton>
      </ViewToggle>

      {userStats && (
        <UserStatsCard>
          <StatItem>
            <StatValue>#{userStats.rank}</StatValue>
            <StatLabel>Your Rank</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>{userStats.total_score}</StatValue>
            <StatLabel>Total Score</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>{userStats.total_searches}</StatValue>
            <StatLabel>Searches</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>{userStats.concepts_clicked}</StatValue>
            <StatLabel>Concepts</StatLabel>
          </StatItem>
          <StatItem>
            <StatValue>{userStats.time_spent_minutes}m</StatValue>
            <StatLabel>Active Time Spent</StatLabel>
          </StatItem>
        </UserStatsCard>
      )}

      <LeaderboardTable>
        <TableHeader>
          <div style={{ textAlign: 'center' }}>Rank</div>
          <div>Explorer</div>
          <div style={{ textAlign: 'right' }}>Score</div>
          <div style={{ textAlign: 'right' }}>Searches</div>
          <div style={{ textAlign: 'right' }}>Concepts</div>
          <div style={{ textAlign: 'right' }}>Time</div>
        </TableHeader>

        {leaderboard.map(entry => {
          const isW1Winner = WEEK_1_WINNERS.includes(entry.displayName) || 
                            entry.displayName.startsWith('the.ghost.with.extra.');
          const isW2Winner = WEEK_2_WINNERS.includes(entry.displayName);
          const isW3Winner = WEEK_3_WINNERS.includes(entry.displayName);
          const badgeText = isW1Winner ? 'W1' : isW2Winner ? 'W2' : isW3Winner ? 'W3' : null;
          
          return (
            <LeaderboardRow 
              key={entry.userId} 
              isCurrentUser={entry.isCurrentUser}
              rank={entry.rank}
            >
              {/* Desktop Layout */}
              <Rank rank={entry.rank}>{entry.rank}</Rank>
              <NameWithBadge>
                <NameText>{entry.displayName}</NameText>
                {badgeText && <BadgeContainer><W1Badge>{badgeText}</W1Badge></BadgeContainer>}
              </NameWithBadge>
            <Score>{entry.score}</Score>
            <Stat>{entry.searches}</Stat>
            <Stat>{entry.conceptsClicked}</Stat>
            <Stat>{entry.timeSpent}m</Stat>

            {/* Mobile Card Layout */}
            <MobileCardInfo>
              <MobileCardRow>
                <MobileLabel>
                  <span>#{entry.rank} - {entry.displayName}</span>
                  {badgeText && <W1Badge style={{ marginLeft: '4px' }}>{badgeText}</W1Badge>}
                </MobileLabel>
                <MobileValue style={{ fontSize: 'var(--font-size-lg)', color: 'var(--color-primary)', fontFamily: 'var(--font-heading)' }}>
                  {entry.score} pts
                </MobileValue>
              </MobileCardRow>
              <MobileCardRow>
                <MobileLabel>Searches</MobileLabel>
                <MobileValue>{entry.searches}</MobileValue>
              </MobileCardRow>
              <MobileCardRow>
                <MobileLabel>Concepts</MobileLabel>
                <MobileValue>{entry.conceptsClicked}</MobileValue>
              </MobileCardRow>
              <MobileCardRow>
                <MobileLabel>Time Spent</MobileLabel>
                <MobileValue>{entry.timeSpent}m</MobileValue>
              </MobileCardRow>
            </MobileCardInfo>
          </LeaderboardRow>
          );
        })}
      </LeaderboardTable>
    </Container>
  );
};
